import { useQuery } from '@tanstack/react-query';
import { authEventTypeLabel } from '../../../lib/auth-event-labels';
import { fetchMyEvents } from '../api';

const KEY = ['my-auth-events'] as const;

const eventBadgeClass = (type: string): string => {
  if (type.startsWith('login.success') || type === 'register.success' || type === 'refresh.success')
    return 'border-emerald-200 bg-emerald-50 text-emerald-950 ring-emerald-900/20';
  if (type === 'login.failed' || type === 'register.failed' || type === 'refresh.expired')
    return 'border-amber-200 bg-amber-50 text-amber-950 ring-amber-950/25';
  if (type === 'login.locked' || type === 'lockout.triggered' || type === 'refresh.replay_detected')
    return 'border-red-300 bg-red-50 text-red-950 ring-red-900/15';
  if (type === 'device.new' || type === 'device.removed' || type === 'password.changed')
    return 'border-sky-200 bg-sky-50 text-sky-950 ring-sky-900/20';
  return 'border-slate-200 bg-slate-50 text-slate-900 ring-slate-900/10';
};

export function RecentActivitySection() {
  const { data, isLoading, error } = useQuery({
    queryKey: KEY,
    queryFn: () => fetchMyEvents({ page: 1, limit: 20 }),
  });

  return (
    <section className="app-card overflow-hidden">
      <div className="border-b border-slate-900/10 px-8 py-7">
        <h3 className="text-[0.9rem] font-semibold uppercase tracking-[0.11em] text-slate-800">
          Activité récente
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          Les vingt derniers événements liés à l’authentification sur votre compte.
        </p>
      </div>
      <div className="divide-y divide-slate-100">
        {isLoading && (
          <p className="py-12 text-center text-sm font-medium text-slate-600">Chargement…</p>
        )}
        {error && (
          <p className="py-10 text-center text-error px-8">Impossible de charger l’activité.</p>
        )}
        {data && data.data.length === 0 && (
          <p className="py-12 px-8 text-center text-sm text-slate-500">
            Aucun événement notable pour l’instant.
          </p>
        )}
        {data?.data.map((event) => (
          <div
            key={event.id}
            className="flex flex-col gap-3 px-8 py-5 md:grid md:grid-cols-[minmax(0,220px)_1fr_auto] md:items-center md:gap-8"
          >
            <span
              className={`inline-flex max-w-fit rounded-xl border px-3 py-1 font-mono text-xs font-semibold ring-1 ${eventBadgeClass(event.eventType)}`}
            >
              {authEventTypeLabel(event.eventType)}
            </span>
            <p className="text-sm text-slate-600 md:truncate">
              <span className="font-mono text-xs text-slate-500">{event.ip ?? '—'} </span>
              <span className="text-slate-400"> · </span>
              <span>{(event.userAgent ?? '—').slice(0, 80)}</span>
            </p>
            <time
              dateTime={event.createdAt}
              className="text-xs font-medium whitespace-nowrap text-slate-500 md:text-right"
            >
              {new Date(event.createdAt).toLocaleString()}
            </time>
          </div>
        ))}
      </div>
    </section>
  );
}
